import { useId } from "react";
import { cn } from "../cn";
import type { LogoColor, LogoVariant } from "./one-nexty";
import { NextyWordmark } from "./nexty-brand";

export interface NextyProductLogoProps extends React.SVGProps<SVGSVGElement> {
  variant?: LogoVariant;
  color?: LogoColor;
  size?: number;
  title?: string;
  className?: string;
}

// ============================================================================
// 1. NEXTY DESIGN LOGO
// ============================================================================

/**
 * Symbole / Emblème Produit nexty. design (Empilement Isométrique 3D)
 */
export function NextyDesignSystemMark({
  color = "primary",
  size = 32,
  title = "nexty design Mark",
  className,
  ...props
}: Omit<NextyProductLogoProps, "variant">) {
  const rawGradientId = useId();
  const rawFillGradientId = useId();
  const rawDotGlowId = useId();

  const gradientId = rawGradientId.replace(/:/g, "_");
  const fillGradientId = rawFillGradientId.replace(/:/g, "_");
  const dotGlowId = rawDotGlowId.replace(/:/g, "_");

  const isGradient = color === "gradient" || color === "primary";
  const isMono = color === "monochrome";
  const isWhite = color === "white";

  const strokeColor = isWhite ? "#FFFFFF" : isMono ? "currentColor" : `url(#${gradientId})`;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 36 36"
      fill="none"
      width={size}
      height={size}
      aria-hidden={title ? undefined : true}
      role={title ? "img" : undefined}
      className={cn("inline-block shrink-0 align-middle", className)}
      {...props}
    >
      {title && <title>{title}</title>}
      <defs>
        {/* Dégradé Bleu Royal SOTA */}
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#2563EB" />
          <stop offset="50%" stopColor="#3B82F6" />
          <stop offset="100%" stopColor="#1D4ED8" />
        </linearGradient>

        <linearGradient id={fillGradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#2563EB" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#3B82F6" stopOpacity="0.1" />
        </linearGradient>

        {/* Dégradé radial pour la diffusion douce du halo lumineux */}
        <radialGradient id={dotGlowId} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#60A5FA" stopOpacity="0.85" />
          <stop offset="35%" stopColor="#3B82F6" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#2563EB" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Calque 1 (Supérieur) : Composants UI */}
      <path
        d="M18 6L28 11.5L18 17L8 11.5Z"
        stroke={strokeColor}
        strokeWidth="2.4"
        strokeLinejoin="round"
        fill={isGradient ? `url(#${fillGradientId})` : "none"}
      />

      {/* Calque 2 (Médian) : Tokens & Sémantique */}
      <path
        d="M8 17.5L18 23L28 17.5"
        stroke={strokeColor}
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Calque 3 (Base) : Primitives */}
      <path
        d="M8 23.5L18 29L28 23.5"
        stroke={strokeColor}
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Halo lumineux véritablement diffus (fond évanescent vers 0% d'opacité) */}
      {isGradient && (
        <circle cx="29" cy="7.5" r="5.5" fill={`url(#${dotGlowId})`} />
      )}

      {/* Point bleu royal signature */}
      <circle cx="29" cy="7.5" r="2.25" fill="#2563EB" />
    </svg>
  );
}

export function NextyDesignSystemLogo({
  variant = "full",
  color = "primary",
  size = 32,
  className,
  ...props
}: NextyProductLogoProps) {
  if (variant === "mark") {
    return <NextyDesignSystemMark color={color} size={size} className={className} {...props} />;
  }

  const isStacked = variant === "stacked";
  const isWhite = color === "white";
  const isMono = color === "monochrome";

  return (
    <div
      className={cn(
        "inline-flex items-center gap-2.5 select-none",
        isStacked ? "flex-col text-center gap-1.5" : "flex-row",
        className
      )}
    >
      <NextyDesignSystemMark color={color} size={size} {...props} />
      <div className="inline-flex items-baseline gap-1.5 font-sans">
        <NextyWordmark color={color} size={size * 0.85} />
        <span
          className={cn(
            "font-serif italic font-normal tracking-wide",
            isWhite
              ? "text-blue-200"
              : isMono
              ? "text-current"
              : "bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-300 bg-clip-text text-transparent"
          )}
          style={{ fontSize: `${size * 0.78}px` }}
        >
          design
        </span>
      </div>
    </div>
  );
}

// ============================================================================
// 2. NEXTY ARTHUR LOGO
// ============================================================================

/**
 * Symbole / Emblème Produit nexty. arthur (AI Spark Core & Monogramme A)
 */
export function NextyArthurMark({
  color = "primary",
  size = 32,
  title = "nexty arthur Mark",
  className,
  ...props
}: Omit<NextyProductLogoProps, "variant">) {
  const rawGradientId = useId();
  const rawDotGlowId = useId();

  const gradientId = rawGradientId.replace(/:/g, "_");
  const dotGlowId = rawDotGlowId.replace(/:/g, "_");

  const isGradient = color === "gradient" || color === "primary";
  const isMono = color === "monochrome";
  const isWhite = color === "white";

  const strokeColor = isWhite ? "#FFFFFF" : isMono ? "currentColor" : `url(#${gradientId})`;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 36 36"
      fill="none"
      width={size}
      height={size}
      aria-hidden={title ? undefined : true}
      role={title ? "img" : undefined}
      className={cn("inline-block shrink-0 align-middle", className)}
      {...props}
    >
      {title && <title>{title}</title>}
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#DB2777" />
          <stop offset="50%" stopColor="#7C3AED" />
          <stop offset="100%" stopColor="#2563EB" />
        </linearGradient>
        <radialGradient id={dotGlowId} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#60A5FA" stopOpacity="0.85" />
          <stop offset="35%" stopColor="#3B82F6" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#2563EB" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Monogramme 'A' vectoriel dynamique */}
      <path
        d="M18 6L9 28H13.5L18 17.5L22.5 28H27L18 6Z"
        stroke={strokeColor}
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M13.5 20H22.5"
        stroke={strokeColor}
        strokeWidth="2.2"
        strokeLinecap="round"
      />
      {/* Halo lumineux diffus */}
      {isGradient && (
        <circle cx="28" cy="8" r="5.5" fill={`url(#${dotGlowId})`} />
      )}
      {/* Spark Core AI dot */}
      <circle cx="28" cy="8" r="2.25" fill="#2563EB" />
    </svg>
  );
}

export function NextyArthurLogo({
  variant = "full",
  color = "primary",
  size = 32,
  className,
  ...props
}: NextyProductLogoProps) {
  if (variant === "mark") {
    return <NextyArthurMark color={color} size={size} className={className} {...props} />;
  }

  const isStacked = variant === "stacked";
  const isWhite = color === "white";
  const isMono = color === "monochrome";

  const subTextColor = isWhite
    ? "text-slate-200"
    : isMono
    ? "text-current/80"
    : "text-rose-600 dark:text-rose-400 font-semibold";

  return (
    <div
      className={cn(
        "inline-flex items-center gap-2.5 select-none",
        isStacked ? "flex-col text-center gap-1.5" : "flex-row",
        className
      )}
    >
      <NextyArthurMark color={color} size={size} {...props} />
      <div className="inline-flex items-baseline gap-1.5 font-sans">
        <NextyWordmark color={color} size={size * 0.85} />
        <span
          className={cn("tracking-tight", subTextColor)}
          style={{ fontSize: `${size * 0.72}px` }}
        >
          arthur
        </span>
      </div>
    </div>
  );
}

// ============================================================================
// 3. NEXTY CLOUD LOGO
// ============================================================================

/**
 * Symbole / Emblème Produit nexty. cloud (Nuit / Cloud Infrastructure Mesh)
 */
export function NextyCloudMark({
  color = "primary",
  size = 32,
  title = "nexty cloud Mark",
  className,
  ...props
}: Omit<NextyProductLogoProps, "variant">) {
  const rawGradientId = useId();
  const rawDotGlowId = useId();

  const gradientId = rawGradientId.replace(/:/g, "_");
  const dotGlowId = rawDotGlowId.replace(/:/g, "_");

  const isGradient = color === "gradient" || color === "primary";
  const isMono = color === "monochrome";
  const isWhite = color === "white";

  const strokeColor = isWhite ? "#FFFFFF" : isMono ? "currentColor" : `url(#${gradientId})`;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 36 36"
      fill="none"
      width={size}
      height={size}
      aria-hidden={title ? undefined : true}
      role={title ? "img" : undefined}
      className={cn("inline-block shrink-0 align-middle", className)}
      {...props}
    >
      {title && <title>{title}</title>}
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#0284C7" />
          <stop offset="50%" stopColor="#2563EB" />
          <stop offset="100%" stopColor="#4F46E5" />
        </linearGradient>
        <radialGradient id={dotGlowId} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#60A5FA" stopOpacity="0.85" />
          <stop offset="35%" stopColor="#3B82F6" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#2563EB" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Nuage d'infrastructure interconnecté */}
      <path
        d="M10 24.5C7.24 24.5 5 22.26 5 19.5C5 17.03 6.78 14.98 9.17 14.58C9.72 10.8 12.98 8 16.8 8C20.65 8 23.9 10.84 24.42 14.65C26.97 15.01 29 17.15 29 19.8C29 22.4 26.9 24.5 24.3 24.5H10Z"
        stroke={strokeColor}
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Nœud réseau central */}
      <circle cx="16.8" cy="16" r="2.5" fill={isWhite ? "#FFFFFF" : isMono ? "currentColor" : "#0284C7"} />
      {/* Halo lumineux diffus */}
      {isGradient && (
        <circle cx="28" cy="8" r="5.5" fill={`url(#${dotGlowId})`} />
      )}
      {/* Point bleu royal signature */}
      <circle cx="28" cy="8" r="2.25" fill="#2563EB" />
    </svg>
  );
}

export function NextyCloudLogo({
  variant = "full",
  color = "primary",
  size = 32,
  className,
  ...props
}: NextyProductLogoProps) {
  if (variant === "mark") {
    return <NextyCloudMark color={color} size={size} className={className} {...props} />;
  }

  const isStacked = variant === "stacked";
  const isWhite = color === "white";
  const isMono = color === "monochrome";

  const subTextColor = isWhite
    ? "text-slate-200"
    : isMono
    ? "text-current/80"
    : "text-sky-600 dark:text-sky-300 font-semibold";

  return (
    <div
      className={cn(
        "inline-flex items-center gap-2.5 select-none",
        isStacked ? "flex-col text-center gap-1.5" : "flex-row",
        className
      )}
    >
      <NextyCloudMark color={color} size={size} {...props} />
      <div className="inline-flex items-baseline gap-1.5 font-sans">
        <NextyWordmark color={color} size={size * 0.85} />
        <span
          className={cn("tracking-tight", subTextColor)}
          style={{ fontSize: `${size * 0.72}px` }}
        >
          cloud
        </span>
      </div>
    </div>
  );
}
